import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTabs } from '../store/tabs'
import { useBulk } from '../store/bulk'
import {
  buildCorrelationInput,
  runCorrelation,
  type CorrelationInput,
  type CorrelationVerdict,
} from '../utils/correlateAi'
import type { AiProvider } from '../utils/aiNames'

const PROVIDERS: { key: AiProvider; label: string }[] = [
  { key: 'openai', label: 'OpenAI' },
  { key: 'anthropic', label: 'Anthropic' },
  { key: 'gemini', label: 'Gemini' },
  { key: 'xai', label: 'xAI Grok' },
]

function pctClass(n: number) {
  if (n >= 0.8) return 'bg-rose-500/20 text-rose-100 border-rose-400/40'
  if (n >= 0.55) return 'bg-amber-400/20 text-amber-100 border-amber-400/40'
  if (n >= 0.3) return 'bg-yellow-400/15 text-yellow-100 border-yellow-400/30'
  return 'bg-emerald-500/15 text-emerald-100 border-emerald-400/30'
}

export function Correlate() {
  const navigate = useNavigate()
  const tabs = useTabs((s) => s.tabs)
  const bulk = useBulk()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [input, setInput] = useState<CorrelationInput | null>(null)
  const [verdict, setVerdict] = useState<CorrelationVerdict | null>(null)

  const eligible = useMemo(() => tabs.length >= 2, [tabs.length])

  async function previewOnly() {
    setBusy(true)
    setError(null)
    setVerdict(null)
    try {
      const r = await buildCorrelationInput(tabs)
      setInput(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function runAi() {
    setBusy(true)
    setError(null)
    try {
      const r = await runCorrelation({
        handles: tabs,
        provider: bulk.provider,
        apiKey: bulk.apiKey,
        model: bulk.model || undefined,
      })
      setInput(r.input)
      setVerdict(r.verdict)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="mx-auto mt-4 max-w-6xl px-4 pb-16">
      <div className="glass p-5">
        <div className="mb-2 flex items-baseline justify-between">
          <h1 className="text-xl font-extrabold tracking-tight">AI correlation across open tabs</h1>
          <button type="button" className="glass-button text-xs" onClick={() => navigate('/')}>
            ✕ close
          </button>
        </div>
        <p className="mb-4 text-sm text-ink-200">
          Reads every currently-open tab from your local cache, builds a feature summary
          (totals, hour-of-day pattern, sub-second action bursts, sample post text, top
          hashtags, top reply targets, pairwise ms/second timestamp overlap) and asks the AI
          to estimate likelihood that any pair (or all of them) is the same person /
          operator.
          <br />
          <span className="text-[11px] text-ink-400">
            All inputs are local-cache only. The AI key you provide on{' '}
            <Link className="accent-text underline" to="/bulk">/bulk</Link> is reused. Output
            is the model's opinion — verify before acting on it.
          </span>
        </p>

        <div className="mb-4 grid gap-2 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            provider
            <select
              className="glass-input"
              value={bulk.provider}
              onChange={(e) => bulk.setProvider(e.target.value as AiProvider)}
            >
              {PROVIDERS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            api key
            <input
              type="password"
              className="glass-input"
              value={bulk.apiKey}
              onChange={(e) => bulk.setApiKey(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            model override (optional)
            <input
              className="glass-input"
              value={bulk.model}
              onChange={(e) => bulk.setModel(e.target.value)}
            />
          </label>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-ink-300">
          <strong className="text-white">{tabs.length}</strong> open tab{tabs.length === 1 ? '' : 's'}:{' '}
          {tabs.map((h) => (
            <span key={h} className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5">
              @{h}
            </span>
          ))}
          {!eligible ? (
            <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-amber-100">
              open at least two profiles in tabs first
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="glass-button"
            onClick={previewOnly}
            disabled={!eligible || busy}
          >
            {busy && !verdict ? 'gathering…' : 'preview features'}
          </button>
          <button
            type="button"
            className="glass-button glass-button--accent"
            onClick={runAi}
            disabled={!eligible || busy || !bulk.apiKey}
          >
            {busy ? 'asking AI…' : 'run AI correlation'}
          </button>
        </div>

        {error ? (
          <div className="mt-3 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
            {error}
          </div>
        ) : null}
      </div>

      {verdict ? (
        <div className="glass-tinted mt-3 p-4 text-sm">
          <h2 className="mb-2 text-lg font-bold">AI verdict</h2>
          <div className="space-y-3">
            {verdict.pairs.map((p, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-black/20 p-3">
                <div className="mb-1 flex flex-wrap items-baseline gap-3">
                  <span className="font-semibold">@{p.a} ↔ @{p.b}</span>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-xs ${pctClass(p.likelihoodSamePerson)}`}
                  >
                    {(p.likelihoodSamePerson * 100).toFixed(0)}% likely same person · {p.confidence}
                  </span>
                </div>
                <p className="text-xs text-ink-200">{p.reasoning}</p>
                {p.signals?.length ? (
                  <ul className="mt-2 list-disc pl-5 text-[11px] text-ink-300">
                    {p.signals.map((s, k) => (
                      <li key={k}>{s}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
          {verdict.groupVerdict ? (
            <div className="mt-4 rounded-xl border border-violet-400/30 bg-violet-500/10 p-3 text-xs">
              <div className="mb-1 flex items-baseline gap-2">
                <strong className="text-sm">all-{tabs.length} verdict:</strong>
                <span
                  className={`rounded-full border px-2 py-0.5 text-xs ${pctClass(verdict.groupVerdict.likelihoodAllSame)}`}
                >
                  {(verdict.groupVerdict.likelihoodAllSame * 100).toFixed(0)}% likely all same
                </span>
              </div>
              <p className="text-ink-200">{verdict.groupVerdict.reasoning}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      {input ? (
        <details className="glass mt-3 p-4 text-xs">
          <summary className="cursor-pointer text-sm font-bold">
            raw features (what the AI saw)
          </summary>
          <pre className="mt-2 max-h-[60vh] overflow-auto whitespace-pre-wrap break-all text-[10px] text-ink-300">
            {JSON.stringify(input, null, 2)}
          </pre>
        </details>
      ) : null}
    </main>
  )
}
